import React from "react";
import { Edit3 } from "lucide-react";
import type {
  BaseEntity,
  SimpleFieldConfig,
  SimpleTableConfig,
} from "../../UniversalCRMTable/types";

export interface FieldBasedBulkAction<T extends BaseEntity> {
  id: string;
  label: string;
  icon: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost";
  onClick: (selectedItems: T[], selectedIds: string[]) => Promise<void>;
  disabled?: (selectedItems: T[]) => boolean;
  hidden?: (selectedItems: T[]) => boolean;
}

/**
 * Generate bulk actions from field configuration
 */
export function generateFieldBasedBulkActions<T extends BaseEntity>(
  config: SimpleTableConfig<T>,
  onBulkEditField: (fieldId: string, selectedItems: T[], selectedIds: string[]) => void
): FieldBasedBulkAction<T>[] {
  const bulkActions: FieldBasedBulkAction<T>[] = [];

  // Get all bulk editable fields
  Object.entries(config.fields).forEach(([fieldId, fieldConfig]) => {
    if (fieldConfig.bulkEditable && !fieldConfig.formOnly) {
      bulkActions.push({
        id: `bulk-edit-${fieldId}`,
        label: `Edit ${fieldConfig.label}`,
        icon: <Edit3 size={16} />,
        variant: "outline",
        onClick: async (selectedItems, selectedIds) => {
          onBulkEditField(fieldId, selectedItems, selectedIds);
        },
      });
    }
  });

  return bulkActions;
}

/**
 * Get bulk editable fields from config
 */
export function getBulkEditableFields<T extends BaseEntity>(
  config: SimpleTableConfig<T>
): Array<{ fieldId: string; fieldConfig: SimpleFieldConfig }> {
  return Object.entries(config.fields)
    .filter(([_, fieldConfig]) => fieldConfig.bulkEditable && !fieldConfig.formOnly)
    .map(([fieldId, fieldConfig]) => ({ fieldId, fieldConfig }));
}

/**
 * Check if a field is bulk editable
 */
export function isFieldBulkEditable(fieldConfig: SimpleFieldConfig): boolean {
  return Boolean(fieldConfig.bulkEditable && !fieldConfig.formOnly);
}

/**
 * Get the display label for a bulk edit action
 */
export function getBulkEditActionLabel(fieldConfig: SimpleFieldConfig): string {
  return `Edit ${fieldConfig.label}`;
}

/**
 * Get the action ID for a bulk edit action
 */
export function getBulkEditActionId(fieldId: string): string {
  return `bulk-edit-${fieldId}`;
}
